import React from 'react';
import { format, isToday, isYesterday } from 'date-fns';
import { fr } from 'date-fns/locale';
import { MagnifyingGlassIcon, PlusIcon } from '@heroicons/react/24/outline';
import type { Conversation } from '../types/sms.types';

interface ConversationListProps {
  conversations: Conversation[];
  selectedPhone: string | null;
  onSelect: (phoneNumber: string) => void;
  onNewConversation: () => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  loading?: boolean;
}

export const ConversationList: React.FC<ConversationListProps> = ({
  conversations,
  selectedPhone,
  onSelect,
  onNewConversation,
  searchQuery,
  onSearchChange,
  loading = false
}) => {
  const formatTime = (timestamp: string) => {
    try {
      // Timestamp comes as 'YYYY-MM-DD HH:MM:SS' from backend (local time)
      const date = new Date(timestamp.replace(' ', 'T'));
      if (isToday(date)) {
        return format(date, 'HH:mm', { locale: fr });
      }
      if (isYesterday(date)) {
        return 'Hier';
      }
      return format(date, 'dd/MM', { locale: fr });
    } catch {
      return '';
    }
  };

  const truncateMessage = (message: string, maxLength = 40) => {
    if (message.length <= maxLength) return message;
    return message.substring(0, maxLength) + '...';
  };

  return (
    <div className="sms-conversation-list">
      <div className="sms-list-header">
        <div className="sms-search-wrapper">
          <MagnifyingGlassIcon className="sms-search-icon" />
          <input
            type="text"
            className="sms-search-input"
            placeholder="Rechercher..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
          />
        </div>
        <button className="sms-new-btn" onClick={onNewConversation} title="Nouveau SMS">
          <PlusIcon className="sms-new-icon" />
        </button>
      </div>

      <div className="sms-list-content">
        {loading ? (
          <div className="sms-list-loading">
            <div className="sms-loading-spinner" />
            <span>Chargement...</span>
          </div>
        ) : conversations.length === 0 ? (
          <div className="sms-list-empty">
            <p>Aucune conversation</p>
          </div>
        ) : (
          conversations.map((conv) => (
            <div
              key={conv.phoneNumber}
              className={`sms-list-item ${selectedPhone === conv.phoneNumber ? 'sms-list-item-active' : ''}`}
              onClick={() => onSelect(conv.phoneNumber)}
            >
              <div className="sms-list-item-avatar">
                {conv.contactName ? conv.contactName.charAt(0).toUpperCase() : '#'}
              </div>
              <div className="sms-list-item-content">
                <div className="sms-list-item-header">
                  <span className="sms-list-item-name">
                    {conv.contactName || conv.phoneNumber}
                  </span>
                  <span className="sms-list-item-time">
                    {formatTime(conv.lastMessageTime)}
                  </span>
                </div>
                <div className="sms-list-item-preview">
                  {conv.lastDirection === 'outbound' && (
                    <span className="sms-list-item-sent">Vous: </span>
                  )}
                  <span className="sms-list-item-message">
                    {truncateMessage(conv.lastMessage)}
                  </span>
                  {conv.unreadCount > 0 && (
                    <span className="sms-list-item-badge">{conv.unreadCount}</span>
                  )}
                </div>
                {conv.contactName && (
                  <span className="sms-list-item-phone">{conv.phoneNumber}</span>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ConversationList;
